import { create } from 'zustand'
import type { CallerInformationFormValues } from '@/types/cases'
import type { AmbulanceRequestInformationFormValues } from '@/types/cases'

/**
 * Case Creation State Interface
 *
 * This interface defines the structure for storing all case creation related data.
 * It's designed to be easily extensible for future form sections.
 */
interface CaseCreationState {
  // Caller Information Data
  callerInformation: Partial<CallerInformationFormValues> | null

  // Ambulance Request Information Data
  ambulanceRequestInformation: Partial<AmbulanceRequestInformationFormValues> | null

  // Notes - optional dispatcher notes for the case
  notes: string

  // Additional case metadata
  currentSection: 'caller-information' | 'ambulance-request-information' | null
  isSubmitting: boolean
  lastUpdated: Date | null
}

/**
 * Case Creation Actions Interface
 *
 * Defines all available actions for managing case creation state.
 * Each action is properly typed for type safety.
 */
interface CaseCreationActions {
  // Caller Information Actions
  setCallerInformation: (data: CallerInformationFormValues) => void
  updateCallerField: <K extends keyof CallerInformationFormValues>(
    field: K,
    value: CallerInformationFormValues[K]
  ) => void
  updateMultipleCallerFields: (
    updates: Partial<CallerInformationFormValues>
  ) => void

  // Ambulance Request Information Actions
  setAmbulanceRequestInformation: (
    data: AmbulanceRequestInformationFormValues
  ) => void
  updateAmbulanceField: <K extends keyof AmbulanceRequestInformationFormValues>(
    field: K,
    value: AmbulanceRequestInformationFormValues[K]
  ) => void
  updateMultipleAmbulanceFields: (
    updates: Partial<AmbulanceRequestInformationFormValues>
  ) => void

  // Notes Actions
  setNotes: (notes: string) => void

  // General Case Management Actions
  setCurrentSection: (section: CaseCreationState['currentSection']) => void
  setSubmitting: (isSubmitting: boolean) => void
  clearCase: () => void

  // Utility Actions
  getCaseData: () => {
    callerInformation: Partial<CallerInformationFormValues> | null
    ambulanceRequestInformation: Partial<AmbulanceRequestInformationFormValues> | null
    notes: string
    currentSection: CaseCreationState['currentSection']
    isSubmitting: boolean
    lastUpdated: Date | null
  }
  hasCallerData: () => boolean
  hasAmbulanceData: () => boolean
  isComplete: () => boolean
}

/**
 * Combined Store Type
 *
 * Combines state and actions into a single store interface.
 */
type CaseCreationStore = CaseCreationState & CaseCreationActions

/**
 * Default initial state for the case creation store
 */
const initialState: CaseCreationState = {
  callerInformation: null,
  ambulanceRequestInformation: null,
  notes: '',
  currentSection: null,
  isSubmitting: false,
  lastUpdated: null,
}

/**
 * Case Creation Store
 *
 * A Zustand store for managing case creation state across the application.
 * Provides type-safe actions for managing caller information, ambulance request data,
 * and general case creation workflow.
 *
 * Features:
 * - Type-safe field updates
 * - Batch updates for multiple fields
 * - Complete case data management
 * - Workflow state tracking
 * - Easy extensibility for new form sections
 */
export const useCaseCreationStore = create<CaseCreationStore>()((set, get) => ({
  // Initial State
  ...initialState,

  // Caller Information Actions

  /**
   * Sets the complete caller information data
   * @param data - Complete caller information form values
   */
  setCallerInformation: (data: CallerInformationFormValues) => {
    set({
      callerInformation: data,
      lastUpdated: new Date(),
    })
  },

  /**
   * Updates a single field in caller information
   * @param field - The field name to update
   * @param value - The new value for the field
   */
  updateCallerField: <K extends keyof CallerInformationFormValues>(
    field: K,
    value: CallerInformationFormValues[K]
  ) => {
    set(state => ({
      callerInformation: {
        ...state.callerInformation,
        [field]: value,
      },
      lastUpdated: new Date(),
    }))
  },

  /**
   * Updates multiple fields in caller information at once
   * @param updates - Object containing field-value pairs to update
   */
  updateMultipleCallerFields: (
    updates: Partial<CallerInformationFormValues>
  ) => {
    set(state => ({
      callerInformation: {
        ...state.callerInformation,
        ...updates,
      },
      lastUpdated: new Date(),
    }))
  },

  // Ambulance Request Information Actions

  /**
   * Sets the complete ambulance request information data
   * @param data - Complete ambulance request information form values
   */
  setAmbulanceRequestInformation: (
    data: AmbulanceRequestInformationFormValues
  ) => {
    set({
      ambulanceRequestInformation: data,
      lastUpdated: new Date(),
    })
  },

  /**
   * Updates a single field in ambulance request information
   * @param field - The field name to update
   * @param value - The new value for the field
   */
  updateAmbulanceField: <K extends keyof AmbulanceRequestInformationFormValues>(
    field: K,
    value: AmbulanceRequestInformationFormValues[K]
  ) => {
    set(state => ({
      ambulanceRequestInformation: {
        ...state.ambulanceRequestInformation,
        [field]: value,
      },
      lastUpdated: new Date(),
    }))
  },

  /**
   * Updates multiple fields in ambulance request information at once
   * @param updates - Object containing field-value pairs to update
   */
  updateMultipleAmbulanceFields: (
    updates: Partial<AmbulanceRequestInformationFormValues>
  ) => {
    set(state => ({
      ambulanceRequestInformation: {
        ...state.ambulanceRequestInformation,
        ...updates,
      },
      lastUpdated: new Date(),
    }))
  },

  // Notes Actions

  /**
   * Sets the notes for the case
   * @param notes - The notes text
   */
  setNotes: (notes: string) => {
    set({
      notes,
      lastUpdated: new Date(),
    })
  },

  // General Case Management Actions

  /**
   * Sets the current section in the case creation workflow
   * @param section - The current section identifier
   */
  setCurrentSection: (section: CaseCreationState['currentSection']) => {
    set({ currentSection: section })
  },

  /**
   * Sets the submitting state for the case creation process
   * @param isSubmitting - Whether the case is currently being submitted
   */
  setSubmitting: (isSubmitting: boolean) => {
    set({ isSubmitting })
  },

  /**
   * Clears all case creation data and resets the store to initial state
   * Use this when a case is submitted successfully or when canceling case creation
   */
  clearCase: () => {
    set(initialState)
  },

  // Utility Actions

  /**
   * Gets the complete case data for submission or review
   * @returns Object containing all case creation data
   */
  getCaseData: () => {
    const state = get()
    return {
      callerInformation: state.callerInformation,
      ambulanceRequestInformation: state.ambulanceRequestInformation,
      notes: state.notes,
      currentSection: state.currentSection,
      isSubmitting: state.isSubmitting,
      lastUpdated: state.lastUpdated,
    }
  },

  /**
   * Checks if caller information data exists
   * @returns True if caller information is available
   */
  hasCallerData: () => {
    const state = get()
    return state.callerInformation !== null
  },

  /**
   * Checks if ambulance request information data exists
   * @returns True if ambulance request information is available
   */
  hasAmbulanceData: () => {
    const state = get()
    return state.ambulanceRequestInformation !== null
  },

  /**
   * Checks if the case creation process is complete
   * @returns True if all required data is available
   */
  isComplete: () => {
    const state = get()
    return (
      state.callerInformation !== null &&
      state.ambulanceRequestInformation !== null
    )
  },
}))

/**
 * Hook for accessing case creation store with proper TypeScript inference
 *
 * Usage examples:
 *
 * // Basic usage
 * const { callerInformation, setCallerInformation } = useCaseCreationStore()
 *
 * // Update single field
 * const { updateCallerField } = useCaseCreationStore()
 * updateCallerField('phone_number', '+23490875533')
 *
 * // Update multiple fields
 * const { updateMultipleCallerFields } = useCaseCreationStore()
 * updateMultipleCallerFields({
 *   first_name: 'John',
 *   last_name: 'Doe',
 *   phone_number: '+23490875533'
 * })
 *
 * // Clear all data
 * const { clearCase } = useCaseCreationStore()
 * clearCase()
 */
export default useCaseCreationStore
